import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import Iconify from "minimals-template/components/Iconify"
import { Bound } from "lib/@components/binding/Bound"
import { BoundAutocomplete } from "lib/@components/binding/bound-components"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useRefresh } from "lib/@hooks/useRefresh"
import { getMaintenanceDirectories, moveMaintenanceCategory } from "../../controller/maintenance-library"

const ROOT_CATEGORY_ID = "components"

export function MoveCategoryDialog({ open, onClose, item, onMoved }) {
    const results = getMaintenanceDirectories.useResults()
    const directories = useMemo(() => results || [], [results])
    const parentId = useMemo(getParentId, [item.parentId])
    const options = useMemo(getOptions, [directories, item.id, parentId, item.path])
    const [target] = useState(() => ({ newParentId: "" }))
    const refresh = useRefresh()

    useEffect(resetOnOpen, [open, refresh, target])

    if (!item) return null

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <Bound target={target} refresh={refresh}>
                <DialogTitle>Move "{item.name}"</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Select the destination folder for this {item.type === "file" ? "file" : "folder"}.
                        </Typography>
                        <BoundAutocomplete
                            data-cy="move-destination-autocomplete"
                            field="newParentId"
                            label="Destination Folder"
                            options={options}
                            autoFocus
                            sideEffects
                            extract={(e, v) => v}
                            isOptionEqualToValue={(option, value) => option.id === (value?.id || value)}
                            InputProps={{
                                startAdornment: <SelectionIcon />,
                            }}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} sx={{ display: "flex", alignItems: "center" }}>
                                    {"\u00A0\u00A0\u00A0\u00A0".repeat(option.depth)}
                                    <Iconify
                                        icon="eva:folder-fill"
                                        sx={{ mr: 1, width: 20, height: 20, color: "warning.main", flexShrink: 0 }}
                                    />
                                    {option.name}
                                </Box>
                            )}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <MoveButton onMoved={handleMove} />
                </DialogActions>
            </Bound>
        </Dialog>
    )

    function resetOnOpen() {
        if (open) {
            target.newParentId = ""
            refresh()
        }
    }

    function getOptions() {
        const list = directories.filter(isMoveTarget).map(toOption)
        if (parentId !== ROOT_CATEGORY_ID) {
            list.unshift({ id: ROOT_CATEGORY_ID, name: "Library Root", depth: 0, label: "Library Root" })
        }

        return list

        function isMoveTarget(directory) {
            if (directory.id === ROOT_CATEGORY_ID) return false
            if (directory.id === item.id || directory.id === parentId) return false
            return !directory.path?.startsWith(`${item.path}/`)
        }

        function toOption(directory) {
            // Paths are rooted at "Components", so a top-level folder sits flush against the root.
            const depth = Math.max((directory.path?.split("/").length ?? 1) - 2, 0)
            const indent = "\u00A0\u00A0\u00A0\u00A0".repeat(depth)
            return {
                id: directory.id,
                name: directory.name,
                depth,
                label: `${indent}${directory.name}`,
            }
        }
    }

    function getParentId() {
        return item.parentId ? item.parentId.split(":")[0] : ""
    }

    async function handleMove(newParentId) {
        await moveMaintenanceCategory(item.id, newParentId)
        onMoved()
    }
}

function SelectionIcon() {
    const { target } = useBoundContext()
    if (!target.newParentId) return null
    return <Iconify icon="eva:folder-fill" sx={{ color: "warning.main", width: 20, height: 20, mr: 1 }} />
}

function MoveButton({ onMoved }) {
    const { target } = useBoundContext()
    const newParentId = target.newParentId?.id || target.newParentId

    return (
        <Button
            variant="contained"
            color="primary"
            disabled={!newParentId}
            onClick={() => onMoved(newParentId)}
            data-cy="move-dialog-confirm"
        >
            Move
        </Button>
    )
}
